import { motion } from "framer-motion";
import { Clock, Calendar, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export const CoursesSection = ({ courses = [], loading = false }) => {
  const scrollToEnquiry = () => {
    const element = document.querySelector("#enquiry");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="courses"
      className="section-dark py-24 md:py-32"
      data-testid="courses-section"
    >
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-gold/80 font-manrope text-sm tracking-[0.3em] uppercase mb-4">
            Begin Your Journey
          </p>
          <h2 className="font-cinzel text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6">
            Our <span className="text-gold">Courses</span>
          </h2>
          <div className="section-divider mx-auto" />
        </motion.div>

        {/* Courses Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[#1A1A1A] p-8 space-y-4">
                <Skeleton className="h-8 w-3/4 bg-white/10" />
                <Skeleton className="h-4 w-full bg-white/10" />
                <Skeleton className="h-4 w-full bg-white/10" />
                <Skeleton className="h-4 w-2/3 bg-white/10" />
              </div>
            ))}
          </div>
        ) : courses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course, index) => (
              <motion.div
                key={course.id || index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="course-card p-8 flex flex-col h-full"
                data-testid={`course-card-${index}`}
              >
                {/* Course Title */}
                <h3 className="font-cinzel text-2xl text-gold mb-4">
                  {course.title || "Untitled Course"}
                </h3>

                {/* Description */}
                <p className="font-manrope text-white/60 text-sm leading-relaxed mb-6">
                  {course.description || "Description coming soon."}
                </p>

                {/* Schedule Info */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-gold" />
                    <span className="font-manrope text-white/80 text-sm">
                      {course.duration || "TBD"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-gold" />
                    <span className="font-manrope text-white/80 text-sm">
                      {course.schedule || "TBD"}
                    </span>
                  </div>
                </div>

                {/* Features */}
                {Array.isArray(course.features) && course.features.length > 0 && (
                  <ul className="space-y-2 mb-6 flex-grow">
                    {course.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                        <span className="font-manrope text-white/50 text-sm">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Price & CTA */}
                <div className="mt-auto pt-6 border-t border-white/10">
                  <p className="font-cinzel text-2xl text-gold mb-4">
                    {course.price || "Contact for price"}
                  </p>
                  <Button
                    onClick={scrollToEnquiry}
                    className="w-full btn-gold-outline font-manrope text-sm tracking-wider"
                    data-testid={`enroll-btn-${index}`}
                  >
                    Enquire Now
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <p className="text-center text-white/40 font-manrope text-sm mt-8">
            No courses available at the moment. Please check back later.
          </p>
        )}

        {/* Note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="text-center text-white/40 font-manrope text-sm mt-12"
        >
          * Class timings and fees may vary. Contact us for personalized course recommendations.
        </motion.p>
      </div>
    </section>
  );
};

export default CoursesSection;
